import { useMount, useUnmount, useUpdateEffect } from 'ahooks';
import * as Sentry from '@sentry/react';
import { isAxiosRequestTimeout } from '@agently/utils';
import { ReactNode, useEffectEvent, useMemo, useRef, useState } from 'react';
import { StreamChat, type Event } from 'stream-chat';

import { ChatContext } from './context';

// stream-chat defaults axios to 3000ms; mobile/PWA startup often needs longer.
const STREAM_CHAT_REQUEST_TIMEOUT_MS = 10_000;

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

const shouldReportChatError = (error: unknown) => !isAxiosRequestTimeout(error);

const getConnectedUserId = (client: StreamChat) => client.user?.id ?? null;

const waitForChatUiCleanup = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

export const ChatProvider = ({
  chatTokenId,
  chatUserId,
  children,
}: {
  chatTokenId: null | string;
  chatUserId: null | string;
  children: ReactNode;
}) => {
  const [client, setClient] = useState<null | StreamChat>(null);
  const [error, setError] = useState<Error | null>(null);
  const [unread, setUnread] = useState(0);
  const [streamClient] = useState(() =>
    StreamChat.getInstance(import.meta.env.VITE_CHAT_API_KEY!, {
      timeout: STREAM_CHAT_REQUEST_TIMEOUT_MS,
    }),
  );
  const activeCredentialsRef = useRef<null | {
    tokenId: string;
    userId: string;
  }>(null);
  // Invalidate in-flight connect attempts when credentials or lifecycle change.
  const connectionIdRef = useRef(0);
  const mountedRef = useRef(false);

  const isCurrentConnection = (connectionId: number) =>
    mountedRef.current && connectionIdRef.current === connectionId;

  const invalidateConnection = () => {
    connectionIdRef.current += 1;
    return connectionIdRef.current;
  };

  const handleEvent = useEffectEvent((event: Event) => {
    if (event.total_unread_count !== undefined) {
      setUnread(event.total_unread_count);
    }
  });

  const disconnectClient = async ({
    clearState,
    invalidate,
    reportErrors,
  }: {
    clearState: boolean;
    invalidate: boolean;
    reportErrors: boolean;
  }) => {
    if (invalidate) {
      invalidateConnection();
    }
    streamClient.off(handleEvent);
    activeCredentialsRef.current = null;

    if (clearState && mountedRef.current) {
      setClient(null);
      setUnread(0);
    }

    if (clearState) {
      await waitForChatUiCleanup();
    }

    try {
      if (streamClient.user) {
        await streamClient.disconnectUser();
      }
    } catch (error) {
      if (reportErrors) {
        Sentry.captureException(error);
      }
    }
  };

  const disconnect = useEffectEvent(async () => {
    await disconnectClient({
      clearState: true,
      invalidate: true,
      reportErrors: true,
    });
  });

  const connect = useEffectEvent(async () => {
    const connectionId = invalidateConnection();

    if (!chatTokenId || !chatUserId) {
      await disconnectClient({
        clearState: true,
        invalidate: true,
        reportErrors: true,
      });
      return;
    }

    setError(null);

    try {
      const connectedUserId = getConnectedUserId(streamClient);
      const activeCredentials = activeCredentialsRef.current;
      const isSameConnectedUser = connectedUserId === chatUserId;
      const canReuseConnection =
        isSameConnectedUser &&
        (!activeCredentials ||
          (activeCredentials.userId === chatUserId &&
            activeCredentials.tokenId === chatTokenId));

      if (connectedUserId && !canReuseConnection) {
        // Clear UI before disconnect so Stream components never call APIs on a torn-down client.
        setClient(null);
        await waitForChatUiCleanup();
        if (!isCurrentConnection(connectionId)) {
          return;
        }
        await streamClient.disconnectUser();
        activeCredentialsRef.current = null;
      } else if (!connectedUserId) {
        setClient(null);
      }

      if (!isCurrentConnection(connectionId)) {
        return;
      }

      if (canReuseConnection && streamClient.user) {
        // Keep unread in sync even when Stream client was connected earlier.
        const unreadCount =
          'total_unread_count' in streamClient.user
            ? (streamClient.user.total_unread_count ?? 0)
            : 0;
        setUnread(unreadCount);
        // Ensure we do not register duplicate listeners on re-auth / token refresh.
        streamClient.off(handleEvent);
        streamClient.on(handleEvent);
        setClient(streamClient);
        return;
      }

      const user = await streamClient.connectUser(
        { id: chatUserId },
        chatTokenId,
      );
      if (!user?.me || !isCurrentConnection(connectionId)) {
        if (getConnectedUserId(streamClient) === chatUserId) {
          await streamClient.disconnectUser();
        }
        return;
      }

      activeCredentialsRef.current = {
        tokenId: chatTokenId,
        userId: chatUserId,
      };
      setUnread(user.me.total_unread_count ?? 0);
      // Ensure we do not register duplicate listeners on re-auth / token refresh.
      streamClient.off(handleEvent);
      streamClient.on(handleEvent);
      setClient(streamClient);
    } catch (error) {
      const shouldExposeError = isCurrentConnection(connectionId);
      if (shouldExposeError && shouldReportChatError(error)) {
        Sentry.captureException(error);
      }
      await disconnectClient({
        clearState: true,
        invalidate: true,
        reportErrors: false,
      });
      if (shouldExposeError && mountedRef.current) {
        setError(toError(error));
      }
    }
  });

  useMount(() => {
    mountedRef.current = true;
    void connect();
  });

  useUpdateEffect(() => {
    void connect();
  }, [chatTokenId, chatUserId]);

  useUnmount(() => {
    mountedRef.current = false;
    void disconnect();
  });

  const value = useMemo(
    () => ({ chatUserId, client, error, unread }),
    [client, error, unread, chatUserId],
  );

  return <ChatContext value={value}>{children}</ChatContext>;
};
